// src/pages/TransportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Car,
  Plane,
  Users,
  MapPin,
  Clock,
  Send,
  User,
  Mail,
  Phone,
  Home,
  Wallet,
  CheckCircle2,
} from "lucide-react";
import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import useSpeechToText from "../hooks/useSpeechToText";
import { useSearchParams } from "react-router-dom";
import VoiceDictation from "../components/VoiceDictation";
import useMicLevel from "../hooks/useMicLevel";
import zelijPattern from "../assets/zelij3.png";
import { transportApi } from "../services/transportService";
import { motion } from "framer-motion";

import { t as tr } from "../contexts/translations";
import {
  ModernInput,
  ModernSelect,
  ModernDatePicker,
  ModernCheckbox,
  FormSection
} from "../components/ui/FormComponents";

/* =========================
   API BASE
========================= */
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

const MOROCCAN_CITIES = [
  "Casablanca", "Rabat", "Fès", "Marrakech", "Agadir", "Tanger", "Meknès", "Oujda",
  "Kenitra", "Tétouan", "Safi", "Mohammedia", "Khouribga", "El Jadida", "Béni Mellal",
  "Errachidia", "Taza", "Essaouira", "Ksar el-Kebir", "Settat", "Larache", "Khemisset",
  "Guelmim", "Berrechid", "Wazzane", "Ouarzazate", "Tiznit", "Taouirt", "Nador",
  "Berkane", "Sidi Kacem", "Khénifra", "Sidi Slimane", "Al Hoceima", "Taroudant",
  "Oulad Teima", "Youssoufia", "Fquih Ben Salah", "Ouezzane", "Guercif", "Ifrane",
  "Azrou", "Midelt", "Zagora", "Tinghir", "Chefchaouen", "Autre",
];

/* ---------- Utils dates ---------- */
export const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};



// Removed broken utility functions

// Removed local form components in favor of centralized FormComponents.jsx

const normalizeTime = (t) => {
  if (!t) return "";
  const s = String(t).trim().toLowerCase();
  if (/^\d{1,2}$/.test(s)) return `${String(s).padStart(2, "0")}:00`;
  if (/^\d{1,2}h$/.test(s)) return `${String(s.replace("h", "")).padStart(2, "0")}:00`;
  if (/^\d{1,2}:\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})h(\d{2})$/);
  if (m) return `${String(m[1]).padStart(2, "0")}:${m[2]}`;
  return "";
};

/* ---------- Page ---------- */
const TransportPage = ({ language }) => {
  // ✅ helper translation with fallback
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  // ---------- Speech-to-Text ----------
  const stt = useSpeechToText({
    lang: language === "ar" ? "ar-MA" : language === "en" ? "en-US" : "fr-FR",
    continuous: true,
    interimResults: true,
  });

  useMicLevel(stt.listening);

  useEffect(() => {
    if (!stt.supported) return;
    const full = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (full) console.log("[STT Transport] =>", full);
  }, [stt.supported, stt.transcript, stt.interim]);

  const today = useMemo(() => getToday(), []);

  const [tab, setTab] = useState("trajet");

  // Personal info
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  // Accept
  const [acceptCmi, setAcceptCmi] = useState(false);
  const [formStep, setFormStep] = useState(0);

  // Trajet
  const [villeDepart, setVilleDepart] = useState("");
  const [villeArrivee, setVilleArrivee] = useState("");
  const [dateDepart, setDateDepart] = useState("");
  const [heureDepart, setHeureDepart] = useState("12:00");
  const [nbPersonnes, setNbPersonnes] = useState("1");

  // Navette
  const [aeroport, setAeroport] = useState("");

  // Disposition
  const [lieu, setLieu] = useState("");
  const [dateFin, setDateFin] = useState("");

  // Pré-remplissage depuis la date choisie dans le hero (?dateDepart).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const dd = searchParams.get("dateDepart");
    if (dd) setDateDepart(dd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [typePrestation, setTypePrestation] = useState(T("byHour", "À l'heure"));

  // Autofill Gemini
  const applyTransportFields = (fields) => {
    if (!fields) return;

    if (fields.demandeType && ["trajet", "navette", "disposition"].includes(fields.demandeType)) {
      setTab(fields.demandeType);
    }

    if (fields.date) setDateDepart(fields.date);
    if (fields.heure) {
      const h = normalizeTime(fields.heure);
      if (h) setHeureDepart(h);
    }
    if (fields.nb_personnes != null) setNbPersonnes(String(fields.nb_personnes));

    if (fields.client?.fullName) setFullName(fields.client.fullName);
    if (fields.client?.email) setEmail(fields.client.email);
    if (fields.client?.phone) setPhone(fields.client.phone);
    if (fields.client?.address) setAddress(fields.client.address);

    if (typeof fields.acceptCmi === "boolean") setAcceptCmi(fields.acceptCmi);

    if (fields.demandeType === "trajet") {
      if (fields.villeDepart) setVilleDepart(fields.villeDepart);
      if (fields.villeArrivee) setVilleArrivee(fields.villeArrivee);
    }

    if (fields.demandeType === "navette") {
      if (fields.aeroport) setAeroport(fields.aeroport);
    }

    if (fields.demandeType === "disposition") {
      if (fields.lieuPriseEnCharge) setLieu(fields.lieuPriseEnCharge);
      if (fields.dateRetour) setDateFin(fields.dateRetour);
      if (fields.typePrestation) setTypePrestation(fields.typePrestation);
    }
  };

  const handleGeminiAutofill = async () => {
    const spoken = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (!spoken) return toast.error(T("transportSpeakFirst", "Parle d'abord, puis clique sur Autofill."));

    try {
      const res = await fetch(`${API_BASE}/api/ai/parse-transport`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: spoken, lang: language }),
      });

      const data = await res.json();
      if (!data?.success) {
        toast.error(data?.message || T("geminiError", "Erreur Gemini"));
        console.log("[GEMINI Transport ERROR] =>", data);
        return;
      }

      console.log("[GEMINI Transport JSON] =>", data.fields);
      applyTransportFields(data.fields);
      toast.success(T("geminiAutofillSuccess", "Formulaire transport rempli à partir de ta voix ✅"));
    } catch (e) {
      console.error(e);
      toast.error(T("geminiNetworkError", "Erreur réseau / serveur Gemini"));
    }
  };

  const formTitle = useMemo(() => {
    if (tab === "trajet") return T("simpleTrip", "Trajet Simple");
    if (tab === "navette") return T("airportShuttle", "Navette Aéroport");
    return T("privateHire", "Mise à Disposition");
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    if (!dateDepart) {
      toast.error(T("vChooseDate", "Veuillez choisir la date."));
      return false;
    }
    if (!heureDepart) {
      toast.error(T("vChooseTime", "Veuillez choisir l'heure."));
      return false;
    }

    const n = Number(nbPersonnes);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error(T("vInvalidPeople", "Nombre de personnes invalide."));
      return false;
    }

    if (!fullName.trim()) {
      toast.error(T("vFullName", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!email.trim()) {
      toast.error(T("vEmail", "Veuillez saisir votre email."));
      return false;
    }
    if (!phone.trim()) {
      toast.error(T("vPhone", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!address.trim()) {
      toast.error(T("vAddress", "Veuillez saisir votre adresse."));
      return false;
    }

    if (!acceptCmi) {
      toast.error(T("vAccept", "Veuillez accepter les conditions (CMI/Charte)."));
      return false;
    }

    if (tab === "trajet") {
      if (!villeDepart) {
        toast.error(T("vDepartCity", "Veuillez choisir la ville de départ."));
        return false;
      }
      if (!villeArrivee) {
        toast.error(T("vArriveCity", "Veuillez choisir la ville d'arrivée."));
        return false;
      }
      if (villeDepart === villeArrivee) {
        toast.error(T("vDifferentCities", "Départ et arrivée doivent être différentes."));
        return false;
      }
    }

    if (tab === "navette") {
      if (!aeroport) {
        toast.error(T("vAirport", "Veuillez choisir l'aéroport."));
        return false;
      }
    }

    if (tab === "disposition") {
      if (!lieu.trim()) {
        toast.error(T("vPickup", "Veuillez saisir le lieu de prise en charge."));
        return false;
      }
      if (!dateFin) {
        toast.error(T("vEndDate", "Veuillez choisir la date de fin."));
        return false;
      }
      const a = new Date(dateDepart);
      const b = new Date(dateFin);
      if (a > b) {
        toast.error(T("vEndAfterStart", "La date de fin doit être après la date de début."));
        return false;
      }
    }

    return true;
  };

  const reset = () => {
    setVilleDepart("");
    setVilleArrivee("");
    setAeroport("");
    setLieu("");

    setDateDepart("");
    setHeureDepart("12:00");
    setNbPersonnes("1");

    setDateFin("");
    setTypePrestation(T("byHour", "À l'heure"));

    setFullName("");
    setEmail("");
    setPhone("");
    setAddress("");

    setAcceptCmi(false);

    stt.reset?.();
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Garde-fou wizard : avancer tant qu'on n'est pas à la dernière étape
    if (formStep !== lastFormStep) { formNext(); return; }
    if (!validate()) return;

    try {
      setSubmitting(true);

      // user connecté si existe
      const userJson = localStorage.getItem("user");
      let utilisateurId = null;
      let emailToUse = email;

      if (userJson) {
        const user = JSON.parse(userJson);
        utilisateurId = user?._id || null;
        if (!emailToUse) emailToUse = user?.email || "";
      }

      let payload = {
        demandeType: tab, // "trajet" | "navette" | "disposition"
        date: dateDepart,
        heure: heureDepart,
        nb_personnes: parseInt(nbPersonnes, 10),

        fullName: fullName.trim(),
        email: (emailToUse || "").trim(),
        phone: phone.trim(),
        address: address.trim(),

        utilisateur: utilisateurId || undefined,

        acceptCmi: true,
        acceptCharte: true,
        lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
      };

      if (tab === "trajet") payload = { ...payload, villeDepart, villeArrivee };
      else if (tab === "navette") payload = { ...payload, aeroport };
      else {
        payload = {
          ...payload,
          lieuPriseEnCharge: lieu.trim(),
          dateRetour: dateFin,
          typePrestation,
        };
      }

      const res = await transportApi.createTransportRequest(payload);

      toast.success(
        `${T("transportSent", "✅ Demande envoyée ! Référence:")} ${res?.data?.reference || res?.data?.data?.reference || "N/A"
        }`
      );

      reset();
    } catch (err) {
      console.error(err);
      notifyApiError(err, T("transportError", "Erreur lors de l'envoi de la demande. Veuillez réessayer."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitEnabled = acceptCmi && !submitting;
  const handleOpenCmiInfo = () => {
    window.open("/paiement-securise", "_blank", "noopener,noreferrer");
  };
  const fieldLabelClass =
    "text-[10px] font-black uppercase tracking-[0.16em] text-orange-600";
  const fieldWrapClass = "rounded-xl border-orange-200 bg-white/80 py-2.5";
  const fieldInputClass = "text-sm font-medium text-gray-700";
  const fieldIconClass = "h-4 w-4 text-orange-400";

  // ===== Wizard (demande de transport) =====
  const FORM_STEPS = [
    { label: T("wizTrip", "Trajet"), icon: MapPin },
    { label: T("wizInfos", "Vos infos"), icon: User },
    { label: T("wizPayment", "Paiement"), icon: Wallet },
  ];
  const lastFormStep = FORM_STEPS.length - 1;

  const validateFormStep = (s) => {
    if (s === 0) {
      if (!dateDepart) return T("vChooseDate", "Veuillez choisir la date.");
      if (tab === "trajet" && (!villeDepart || !villeArrivee))
        return T("vCities", "Veuillez choisir les villes de départ et d'arrivée.");
    }
    if (s === 1) {
      if (!fullName.trim()) return T("vFullName", "Veuillez saisir votre nom complet.");
      if (!email.trim()) return T("vEmail", "Veuillez saisir votre email.");
      if (!phone.trim()) return T("vPhone", "Veuillez saisir votre téléphone.");
      if (!address.trim()) return T("vAddress", "Veuillez saisir votre adresse.");
    }
    return null;
  };
  const formNext = () => {
    const err = validateFormStep(formStep);
    if (err) { toast.error(err); return; }
    setFormStep((s) => Math.min(lastFormStep, s + 1));
  };
  const formBack = () => setFormStep((s) => Math.max(0, s - 1));

  return (
    <div className="py-24 min-h-screen relative overflow-hidden" style={{ background: "linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)" }}>
      {/* Pattern zellij */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-10">
        <section className="mb-16">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-orange-700 mb-4 drop-shadow-lg">
              {T("transportTitle", "Transport Touristique")}
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto opacity-90">
              {T("transportSubtitle", "Transferts, navettes et mise à disposition avec chauffeur")}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              { id: "trajet", label: T("simpleTrip", "Trajet Simple"), icon: Car },
              { id: "navette", label: T("airportShuttle", "Navette Aéroport"), icon: Plane },
              { id: "disposition", label: T("privateHire", "Mise à Disposition"), icon: Users },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`
                  flex items-center gap-2 px-7 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] transition-all
                  ${tab === t.id
                    ? "bg-[#0f172a] text-white shadow-xl"
                    : "bg-white/70 text-gray-500 hover:bg-white hover:text-orange-600 shadow-sm"}
                `}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="max-w-6xl mx-auto space-y-8">
            {/* ===== Stepper ===== */}
            <div className="flex items-center justify-between gap-2 mb-2 px-2 max-w-3xl mx-auto">
              {FORM_STEPS.map((st, i) => {
                const Icon = st.icon;
                const done = i < formStep;
                const active = i === formStep;
                return (
                  <React.Fragment key={st.label}>
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300 ${active ? "bg-orange-600 text-white scale-110" : done ? "bg-green-500 text-white" : "bg-white text-gray-400 border border-gray-200"}`}>
                        {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                      </div>
                      <span className={`mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wide ${active ? "text-orange-700" : "text-gray-400"}`}>{st.label}</span>
                    </div>
                    {i < FORM_STEPS.length - 1 && (
                      <div className={`h-1 flex-1 rounded-full mb-6 transition-all duration-300 ${i < formStep ? "bg-green-500" : "bg-gray-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {formStep === 0 && (
            <FormSection
              title={`${T("requestTitle", "Demande")} — ${formTitle}`}
              icon={tab === "trajet" ? Car : tab === "navette" ? Plane : Users}
              className="bg-white/90 border border-orange-100 rounded-[2rem] shadow-[0_12px_30px_rgba(15,23,42,0.08)]"
              headerClassName="mb-5"
              iconWrapClassName="h-10 w-10 p-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white"
              titleClassName="text-[1.9rem] md:text-[2rem] leading-tight font-black uppercase tracking-tight text-[#111827]"
            >
              {/* Voice + Autofill (IA) */}
              <VoiceDictation
                stt={stt}
                onAutofill={handleGeminiAutofill}
                placeholder={T("transportTranscriptPlaceholder", "Parlez pour remplir…")}
                hint={T("transportVoiceHint", "Astuce : dites votre trajet, la date, l'heure et le nombre de personnes.")}
              />

              {tab === "trajet" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <ModernSelect
                    label={T("departureCity", "Ville de départ")}
                    icon={MapPin}
                    value={villeDepart}
                    onChange={(e) => setVilleDepart(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      { value: "", label: T("chooseCity", "Choisir...") },
                      ...MOROCCAN_CITIES.map(c => ({ value: c, label: c }))
                    ]}
                    required
                  />

                  <ModernSelect
                    label={T("arrivalCity", "Ville d'arrivée")}
                    icon={MapPin}
                    value={villeArrivee}
                    onChange={(e) => setVilleArrivee(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      { value: "", label: T("chooseCity", "Choisir...") },
                      ...MOROCCAN_CITIES.map(c => ({ value: c, label: c }))
                    ]}
                    required
                  />

                  <ModernDatePicker
                    label={T("departureDate", "Date")}
                    value={dateDepart}
                    onChange={(e) => setDateDepart(e.target.value)}
                    min={today}
                    language={language}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    inputClassName={fieldInputClass}
                    required
                  />

                  <ModernSelect
                    label={T("departureTime", "Heure")}
                    icon={Clock}
                    value={heureDepart}
                    onChange={(e) => setHeureDepart(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={Array.from({ length: 96 }, (_, i) => {
                      const h = Math.floor(i / 4);
                      const m = (i % 4) * 15;
                      const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                      return { value: time, label: time };
                    })}
                    required
                  />

                  <ModernSelect
                    label={T("guests", "Invités")}
                    icon={Users}
                    value={nbPersonnes}
                    onChange={(e) => setNbPersonnes(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      ...[...Array(10)].map((_, i) => ({ value: String(i + 1), label: String(i + 1) })),
                      { value: "11", label: "11+" }
                    ]}
                    required
                  />
                </div>
              )}

              {tab === "navette" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <ModernSelect
                    label={T("airport", "Aéroport")}
                    icon={Plane}
                    value={aeroport}
                    onChange={(e) => setAeroport(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      { value: "", label: T("allAirports", "Choisir...") },
                      { value: "Marrakech Menara (RAK)", label: "Marrakech Menara (RAK)" },
                      { value: "Casablanca Mohammed V (CMN)", label: "Casablanca Mohammed V (CMN)" },
                      { value: "Fès Saïss (FEZ)", label: "Fès Saïss (FEZ)" },
                      { value: "Agadir Al Massira (AGA)", label: "Agadir Al Massira (AGA)" },
                    ]}
                    required
                  />

                  <ModernDatePicker
                    label={T("departureDate", "Date")}
                    value={dateDepart}
                    onChange={(e) => setDateDepart(e.target.value)}
                    min={today}
                    language={language}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    inputClassName={fieldInputClass}
                    required
                  />

                  <ModernSelect
                    label={T("departureTime", "Heure")}
                    icon={Clock}
                    value={heureDepart}
                    onChange={(e) => setHeureDepart(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={Array.from({ length: 96 }, (_, i) => {
                      const h = Math.floor(i / 4);
                      const m = (i % 4) * 15;
                      const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                      return { value: time, label: time };
                    })}
                    required
                  />

                  <ModernSelect
                    label={T("passengers", "Passagers")}
                    icon={Users}
                    value={nbPersonnes}
                    onChange={(e) => setNbPersonnes(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      ...[...Array(10)].map((_, i) => ({ value: String(i + 1), label: String(i + 1) })),
                      { value: "11", label: "11+" }
                    ]}
                    required
                  />
                </div>
              )}

              {tab === "disposition" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <ModernInput
                    label={T("pickupLocation", "Lieu de prise en charge")}
                    icon={MapPin}
                    placeholder={T("pickupLocationPlaceholder", "Adresse, hôtel...")}
                    value={lieu}
                    onChange={(e) => setLieu(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    inputClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    required
                  />

                  <ModernDatePicker
                    label={T("startDate", "Date de début")}
                    value={dateDepart}
                    onChange={(e) => setDateDepart(e.target.value)}
                    min={today}
                    language={language}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    inputClassName={fieldInputClass}
                    required
                  />

                  <ModernSelect
                    label={T("startTime", "Heure de début")}
                    icon={Clock}
                    value={heureDepart}
                    onChange={(e) => setHeureDepart(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={Array.from({ length: 96 }, (_, i) => {
                      const h = Math.floor(i / 4);
                      const m = (i % 4) * 15;
                      const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                      return { value: time, label: time };
                    })}
                    required
                  />

                  <ModernDatePicker
                    label={T("endDate", "Date de fin")}
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    min={dateDepart || today}
                    language={language}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    inputClassName={fieldInputClass}
                    required
                  />

                  <ModernSelect
                    label={T("serviceType", "Type de prestation")}
                    icon={Clock}
                    value={typePrestation}
                    onChange={(e) => setTypePrestation(e.target.value)}
                    labelClassName={fieldLabelClass}
                    wrapperClassName={fieldWrapClass}
                    selectClassName={fieldInputClass}
                    iconClassName={fieldIconClass}
                    options={[
                      { value: T("byHour", "À l'heure"), label: T("byHour", "À l'heure") },
                      { value: T("halfDay", "Demi-journée"), label: T("halfDay", "Demi-journée") },
                      { value: T("fullDay", "Journée complète"), label: T("fullDay", "Journée complète") },
                      { value: T("severalDays", "Plusieurs jours"), label: T("severalDays", "Plusieurs jours") },
                    ]}
                    required
                  />
                </div>
              )}
            </FormSection>
            )}

            {formStep === 1 && (
            <FormSection
              title={T("personalInfo", "Informations personnelles")}
              icon={User}
              className="bg-white/90 border border-orange-100 rounded-[2rem] shadow-[0_12px_30px_rgba(15,23,42,0.08)]"
              headerClassName="mb-5"
              iconWrapClassName="h-10 w-10 p-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white"
              titleClassName="text-[1.85rem] md:text-[1.95rem] leading-tight font-black uppercase tracking-tight text-[#111827]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ModernInput
                  label={T("fullName", "Nom complet")}
                  icon={User}
                  placeholder={T("fullNamePlaceholder", "Ex: Nacer Hallaoui")}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  labelClassName={fieldLabelClass}
                  wrapperClassName={fieldWrapClass}
                  inputClassName={fieldInputClass}
                  iconClassName={fieldIconClass}
                  required
                />
                <ModernInput
                  label={T("email", "Email")}
                  icon={Mail}
                  type="email"
                  placeholder={T("emailPlaceholder", "vous@example.com")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  labelClassName={fieldLabelClass}
                  wrapperClassName={fieldWrapClass}
                  inputClassName={fieldInputClass}
                  iconClassName={fieldIconClass}
                  required
                />
                <ModernInput
                  label={T("phone", "Téléphone")}
                  icon={Phone}
                  type="tel"
                  placeholder={T("phonePlaceholder", "+212 6xx xx xx xx")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  labelClassName={fieldLabelClass}
                  wrapperClassName={fieldWrapClass}
                  inputClassName={fieldInputClass}
                  iconClassName={fieldIconClass}
                  required
                />
                <ModernInput
                  label={T("address", "Adresse")}
                  icon={Home}
                  placeholder={T("addressPlaceholder", "Ville, quartier, hôtel...")}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  labelClassName={fieldLabelClass}
                  wrapperClassName={fieldWrapClass}
                  inputClassName={fieldInputClass}
                  iconClassName={fieldIconClass}
                  required
                />
              </div>
            </FormSection>
            )}

            {formStep === 2 && (
            <div className="p-7 bg-[#fffaf4]/90 border border-orange-100 rounded-[2rem] space-y-7 max-w-5xl mx-auto shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
              <div>
                <h3 className="text-xl font-black text-orange-700 uppercase tracking-tight mb-4">
                  {T("paymentConditions", "Conditions & Paiement")}
                </h3>
                <ModernCheckbox
                  checked={acceptCmi}
                  onChange={(e) => setAcceptCmi(e.target.checked)}
                >
                  {T("cmiAcceptText", "J’ai lu et j’accepte les Conditions Générales de Vente ainsi que les conditions de paiement via CMI.")}
                </ModernCheckbox>

                <div className="mt-3 relative z-20 flex flex-wrap items-center gap-2 pl-10 pointer-events-auto">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paiement sécurisé :</span>
                  <button
                    type="button"
                    onClick={handleOpenCmiInfo}
                    className="text-xs text-orange-600 font-bold hover:underline cursor-pointer"
                  >
                    Voir les informations CMI / Visa / MasterCard / 3DS
                  </button>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-t border-orange-100 pt-7">
                <div className="flex items-center gap-3 text-xs font-bold text-gray-400 uppercase tracking-widest">
                  <Wallet className="w-4 h-4" />
                  {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
                </div>

                <span className="text-xs text-gray-400">{T("transportStepHint", "Cochez les conditions puis validez ci-dessous.")}</span>
              </div>
            </div>
            )}

            {/* ===== Navigation wizard ===== */}
            <div className="flex items-center justify-between gap-4 max-w-5xl mx-auto">
              {formStep > 0 ? (
                <button
                  type="button"
                  onClick={formBack}
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-black uppercase tracking-widest text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-all"
                >
                  ← {T("wizBack", "Précédent")}
                </button>
              ) : (
                <span />
              )}

              {formStep < lastFormStep ? (
                <button
                  type="button"
                  onClick={formNext}
                  className="inline-flex items-center gap-2 px-10 py-4 bg-[#0f172a] text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg hover:bg-orange-600 transition-all"
                >
                  {T("wizNext", "Suivant")} →
                </button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={!submitEnabled}
                  className={`inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg transition-all ${submitEnabled
                    ? "bg-[#0f172a] text-white hover:bg-orange-600"
                    : "bg-[#e8ebf1] text-[#9aa3b3] cursor-not-allowed"}`}
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                      {T("sending", "Envoi...")}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {T("sendRequest", "Envoyer la demande")}
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default TransportPage;
